import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBoddy, DialogFooter, DialogHeader } from '@/components';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
  isLoading: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteUserModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: InviteUserModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setEmail('');
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    if (!email.trim()) {
      setError(t('users.inviteModal.errors.emailRequired'));
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setError(t('users.inviteModal.errors.emailInvalid'));
      return;
    }

    try {
      await onSubmit(email);
      resetForm();
      onClose();
    } catch {
      setError(t('users.inviteModal.errors.sendFailed'));
    }
  }, [email, onSubmit, onClose, resetForm, t]);

  const handleCancel = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleCancel}>
      <DialogHeader>{t('users.inviteModal.title')}</DialogHeader>
      <DialogBoddy className="pt-2">
        <p className="mb-4 text-sm opacity-50">
          {t('users.inviteModal.description')}
        </p>

        <div className="space-y-4">
          {error && (
            <div className="text-error bg-error/10 rounded-sm px-3 py-2 text-sm">
              {error}
            </div>
          )}
          <div>
            <label className="text-onsurface mb-1 block text-sm font-medium">
              {t('users.inviteModal.emailLabel')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              className="bg-field border-border text-onfield placeholder:text-onfield/30 focus:border-primary focus:ring-primary block w-full rounded-sm border px-2 py-1.5 text-sm focus:z-10 focus:outline-none"
              placeholder={t('users.inviteModal.emailPlaceholder')}
              autoFocus
            />
          </div>
        </div>
      </DialogBoddy>

      <DialogFooter
        abortText={t('common.cancel')}
        submitText={
          isLoading
            ? t('users.inviteModal.sending')
            : t('users.inviteModal.sendButton')
        }
        onSubmit={handleSubmit}
        abortButtonProps={{ disabled: isLoading }}
        submitButtonProps={{ disabled: isLoading }}
      />

      {/* <DialogFooter>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium opacity-50 hover:text-onsurface transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <PrimaryButton onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? t('users.inviteModal.sending') : t('users.inviteModal.sendButton')}
          </PrimaryButton>
        </div>
      </DialogFooter> */}
    </Dialog>
  );
}
